// src/pages/portal/Repairs.tsx
//
// Customer-facing repairs module. Lives inside the portal layout so
// the customer can:
//
//   - See open + closed tickets for their company.
//   - Open a new ticket (SKU + description + serial).
//   - Drill into a single ticket to see the vendor + BENCHMARK
//     audit trail (the agent's statuses, notes, GL pickup no. for
//     return shipments).
//
// Three pages in one file:
//   <RepairsList />     — /portal/repairs
//   <RepairDetail />    — /portal/repairs/:ticketId
//   <NewRepairForm />   — inline expand under the list, mirrors
//                         customer CRUD pattern

import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Wrench,
  Search,
  Plus,
  ChevronLeft,
  Package,
  Truck,
  ChevronRight,
  RotateCcw,
  Sparkles,
  User,
  Check,
  Shield,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { EmptyState } from "@/components/ui/EmptyState";
import { cn } from "@/lib/cn";
import { DEMO_PRODUCTS } from "@/lib/demoData";
import { usePortal } from "@/lib/portal/PortalContext";
import {
  useRepairs,
} from "@/lib/repairs/useRepairs";
import {
  REPAIR_STATUS_ORDER,
  STATUS_TONE,
  WARRANTY_TONE,
  type RepairStatus,
  type RepairTicket,
} from "@/lib/repairs/types";

// Display copy for the repair status / warranty badges. The machine
// values (RepairStatus, RepairWarranty) are untouched.
const REPAIR_STATUS_KEY: Record<RepairStatus, string> = {
  open: "portal.repairs.statusOpen",
  triage: "portal.repairs.statusTriage",
  "vendor-routing": "portal.repairs.statusVendorRouting",
  "in-repair": "portal.repairs.statusInRepair",
  "return-shipped": "portal.repairs.statusReturnShipped",
  closed: "portal.repairs.statusClosed",
};

const WARRANTY_KEY: Record<RepairTicket["warranty"], string> = {
  "in-warranty": "portal.repairs.warrantyUnderVendor",
  "extended-warranty": "portal.repairs.warrantyExtended",
  "out-of-warranty": "portal.repairs.warrantyOut",
};

// ---------- shared helpers ----------

function statusOrderIndex(s: RepairStatus): number {
  return REPAIR_STATUS_ORDER.indexOf(s);
}

function StatusPill({ status }: { status: RepairStatus }) {
  const { t } = useTranslation();
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded px-2 py-0.5 text-[11px] font-510 uppercase tracking-wider",
        STATUS_TONE[status],
      )}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {t(REPAIR_STATUS_KEY[status])}
    </span>
  );
}

function WarrantyPill({ warranty }: { warranty: RepairTicket["warranty"] }) {
  const { t } = useTranslation();
  const Icon =
    warranty === "in-warranty" ? Shield : warranty === "extended-warranty" ? Shield : User;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded px-2 py-0.5 text-[11px] font-510 uppercase tracking-wider",
        WARRANTY_TONE[warranty],
      )}
    >
      <Icon className="h-3 w-3" strokeWidth={2} />
      {t(WARRANTY_KEY[warranty])}
    </span>
  );
}

function ProgressRail({ status }: { status: RepairStatus }) {
  const { t } = useTranslation();
  const idx = statusOrderIndex(status);
  return (
    <div className="flex items-center gap-1">
      {REPAIR_STATUS_ORDER.map((s, i) => {
        const reached = i <= idx;
        const isCurrent = i === idx;
        return (
          <div
            key={s}
            className={cn(
              "h-1 flex-1 rounded-full transition-colors",
              isCurrent
                ? "bg-benchmark-teal-bright"
                : reached
                  ? "bg-benchmark-teal/40"
                  : "bg-bg-elevated",
            )}
            title={t(REPAIR_STATUS_KEY[s])}
          />
        );
      })}
    </div>
  );
}

// ============================================================================
// List
// ============================================================================

export function RepairsList({ basePath = "/portal/repairs" }: { basePath?: `/portal/repairs` | `/admin/repairs` } = {}) {
  const { t } = useTranslation();
  const { tickets, create, reset } = useRepairs();
  const { customer } = usePortal();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [showCreate, setShowCreate] = useState(false);

  // Customer-facing: filter to the current portal customer's tickets.
  // Admins still get the whole queue (they navigate via the admin
  // side, not here).
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return tickets.filter((t) => {
      if (customer && t.customerId !== customer.id) return false;
      if (!q) return true;
      return (
        t.id.toLowerCase().includes(q) ||
        t.sku.toLowerCase().includes(q) ||
        t.productName.toLowerCase().includes(q) ||
        t.description.toLowerCase().includes(q) ||
        (t.serialNumber ?? "").toLowerCase().includes(q)
      );
    });
  }, [tickets, query, customer]);

  const liveCount = visible.filter((t) => t.status !== "closed").length;
  const closedCount = visible.filter((t) => t.status === "closed").length;

  // The product picker for the new-ticket form. We use the seeded
  // DEMO_PRODUCTS so the customer can pick any BENCHMARK SKU; in
  // production it would be filtered by what they've actually bought.
  const productOptions = DEMO_PRODUCTS;

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Wrench
              className="h-5 w-5 text-benchmark-teal-bright"
              strokeWidth={1.5}
            />
            <h2 className="text-xl font-510 tracking-tight text-fg-primary">
              {t("portal.repairs.title")}
            </h2>
          </div>
          <p className="mt-0.5 text-xs text-fg-tertiary">
            {visible.length} {visible.length === 1 ? t("portal.repairs.ticket") : t("portal.repairs.tickets")} ·{" "}
            {liveCount} {t("portal.repairs.live")} · {closedCount} {t("portal.repairs.closed")}. {t("portal.repairs.summaryHint")}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="ghost" size="sm" onClick={reset}>
            <RotateCcw className="h-3.5 w-3.5" strokeWidth={1.5} />
            {t("portal.repairs.resetToSeed")}
          </Button>
          <Button onClick={() => setShowCreate((s) => !s)}>
            <Plus className="h-4 w-4" strokeWidth={1.5} />
            {showCreate ? t("portal.repairs.cancel") : t("portal.repairs.newRepairTicket")}
          </Button>
        </div>
      </header>

      {showCreate && customer && (
        <Card>
          <CardContent className="p-5">
            <NewRepairForm
              customerId={customer.id}
              customerName={customer.companyName}
              products={productOptions}
              onCancel={() => setShowCreate(false)}
              onSubmit={(created) => {
                create(created);
                setShowCreate(false);
                navigate(`/portal/repairs/${created.id}`);
              }}
            />
          </CardContent>
        </Card>
      )}

      {/* Search */}
      <Card>
        <CardContent className="p-3">
          <div className="relative">
            <Search
              className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-fg-tertiary"
              strokeWidth={1.5}
            />
            <Input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("portal.repairs.searchPlaceholder")}
              className="pl-8"
              aria-label={t("portal.repairs.searchLabel")}
            />
          </div>
        </CardContent>
      </Card>

      {/* Tickets */}
      {visible.length === 0 ? (
        <EmptyState
          icon={
            <Wrench
              className="h-5 w-5 text-accent-indigo"
              strokeWidth={1.5}
            />
          }
          title={t("portal.repairs.empty")}
          description={t("portal.repairs.emptyHint")}
        />
      ) : (
        <div className="space-y-2.5">
          {visible.map((t) => (
            <RepairRow key={t.id} t={t} basePath={basePath} />
          ))}
        </div>
      )}
    </div>
  );
}

function RepairRow({
  t,
  basePath,
}: {
  t: RepairTicket;
  basePath: `/portal/repairs` | `/admin/repairs`;
}) {
  const { t: tr } = useTranslation();
  const last = t.timeline[t.timeline.length - 1];
  return (
    <Link
      to={`${basePath}/${t.id}`}
      className="group block rounded-lg border border-border bg-bg-panel transition-colors hover:border-benchmark-teal/40"
    >
      <div className="grid gap-3 p-4 sm:grid-cols-[1.4fr_2fr_1fr_auto] sm:items-center">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[10px] text-fg-quaternary">{t.id}</span>
            <WarrantyPill warranty={t.warranty} />
          </div>
          <div className="mt-0.5 truncate text-sm font-510 text-fg-primary">
            {t.productName}
          </div>
          <div className="truncate text-[11px] text-fg-tertiary">
            <code className="font-mono">{t.sku}</code>
            {t.serialNumber && (
              <>
                <span className="mx-1 text-fg-quaternary">·</span>
                <span className="font-mono">{t.serialNumber}</span>
              </>
            )}
          </div>
        </div>
        <div className="space-y-1.5">
          <ProgressRail status={t.status} />
          <p className="line-clamp-2 text-[11px] text-fg-tertiary">
            {t.description}
          </p>
        </div>
        <div className="space-y-1.5">
          <StatusPill status={t.status} />
          {t.vendor && (
            <div className="flex items-center gap-1.5 text-[11px] text-fg-tertiary">
              <Package className="h-3 w-3" strokeWidth={1.5} />
              <span className="truncate">{t.vendor}</span>
            </div>
          )}
          {t.glsTrackingNumber && (
            <div className="flex items-center gap-1.5 text-[11px] text-fg-tertiary">
              <Truck className="h-3 w-3 text-accent-indigo" strokeWidth={1.5} />
              <span className="font-mono text-fg-secondary">
                {t.glsTrackingNumber}
              </span>
            </div>
          )}
        </div>
        <div className="hidden text-right text-[11px] text-fg-quaternary sm:block">
          {last && <div>{new Date(last.ts).toLocaleDateString()}</div>}
          <div className="mt-1 flex items-center justify-end gap-1 text-fg-tertiary opacity-0 transition-opacity group-hover:opacity-100">
            {tr("portal.repairs.open")}
            <ChevronRight className="h-3 w-3" strokeWidth={1.5} />
          </div>
        </div>
      </div>
    </Link>
  );
}

// ============================================================================
// Detail
// ============================================================================

export function RepairDetail({ basePath = "/portal/repairs" }: { basePath?: `/portal/repairs` | `/admin/repairs` } = {}) {
  const { t: tr } = useTranslation();
  const { ticketId } = useParams<{ ticketId: string }>();
  const { getById } = useRepairs();
  const t = ticketId ? getById(ticketId) : undefined;

  if (!t) {
    return (
      <div className="space-y-4">
        <Link
          to={basePath}
          className="inline-flex items-center gap-1 text-sm text-fg-tertiary hover:text-fg-primary"
        >
          <ChevronLeft className="h-3 w-3" strokeWidth={1.5} />
          {tr("portal.repairs.backToRepairs")}
        </Link>
        <EmptyState
          title={tr("portal.repairs.ticketNotFound")}
          description={tr("portal.repairs.ticketNotFoundHint")} 
          icon={<Wrench className="h-5 w-5 text-fg-quaternary" strokeWidth={1.5} />}
        />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <Link
        to={basePath}
        className="inline-flex items-center gap-1 text-sm text-fg-tertiary hover:text-fg-primary"
      >
        <ChevronLeft className="h-3 w-3" strokeWidth={1.5} />
        {tr("portal.repairs.backToRepairs")}
      </Link>

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs text-fg-quaternary">{t.id}</span>
            <WarrantyPill warranty={t.warranty} />
          </div>
          <h1 className="mt-0.5 text-xl font-510 tracking-tight text-fg-primary">
            {t.productName}
          </h1>
          <div className="mt-1 flex items-center gap-2 text-xs text-fg-tertiary">
            <code className="font-mono">{t.sku}</code>
            {t.serialNumber && (
              <>
                <span className="text-fg-quaternary">·</span>
                <span className="font-mono">SN {t.serialNumber}</span>
              </>
            )}
            {t.vendor && (
              <>
                <span className="text-fg-quaternary">·</span>
                <span className="text-fg-secondary">{t.vendor}</span>
              </>
            )}
          </div>
        </div>
        <StatusPill status={t.status} />
      </header>

      <Card>
        <CardContent className="space-y-3 p-4">
          <ProgressRail status={t.status} />
          <p className="text-sm text-fg-secondary">{t.description}</p>
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <Card>
          <CardContent className="p-5">
            <h3 className="mb-4 text-xs font-510 uppercase tracking-wider text-fg-tertiary">
              {tr("portal.repairs.auditTimeline")}
            </h3>
            <RepairTimeline t={t} />
          </CardContent>
        </Card>

        <RepairSidebar t={t} />
      </div>
    </div>
  );
}

function RepairTimeline({ t }: { t: RepairTicket }) {
  const { t: tr } = useTranslation();
  return (
    <ol className="space-y-3">
      {t.timeline.map((e, i) => (
        <li key={`${t.id}-${i}`} className="flex gap-3">
          <div className="flex flex-col items-center">
            <div
              className={cn(
                "grid h-6 w-6 flex-shrink-0 place-items-center rounded-full ring-2",
                e.actor === "human"
                  ? "bg-benchmark-teal/20 text-benchmark-teal-bright ring-benchmark-teal/40"
                  : e.actor === "agent"
                    ? "bg-accent-indigo/20 text-accent-indigo ring-accent-indigo/40"
                    : "bg-bg-elevated text-fg-tertiary ring-border",
              )}
            >
              {e.actor === "human" ? (
                <User className="h-3 w-3" strokeWidth={2} />
              ) : e.actor === "agent" ? (
                <Sparkles className="h-3 w-3" strokeWidth={2} />
              ) : (
                <span className="h-1.5 w-1.5 rounded-full bg-current" />
              )}
            </div>
            {i !== t.timeline.length - 1 && (
              <div className="my-1 w-px flex-1 bg-border" />
            )}
          </div>
          <div className="-mt-0.5 pb-2">
            <div className="flex items-baseline gap-2">
              <span className="text-sm font-510 text-fg-primary">
                {tr(REPAIR_STATUS_KEY[e.status])}
              </span>
              <span className="font-mono text-[10px] text-fg-quaternary">
                {new Date(e.ts).toLocaleString()}
              </span>
            </div>
            <p className="mt-0.5 text-xs text-fg-tertiary">{e.note}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}

function RepairSidebar({ t }: { t: RepairTicket }) {
  const { t: tr } = useTranslation();
  return (
    <Card>
      <CardContent className="space-y-4 p-5">
        <div className="flex items-center gap-2">
          <Wrench className="h-4 w-4 text-accent-indigo" strokeWidth={1.5} />
          <h3 className="text-xs font-510 uppercase tracking-wider text-fg-tertiary">
            {tr("portal.repairs.repairDetails")}
          </h3>
        </div>

        <KeyValue label={tr("portal.repairs.labelSku")}>
          <code className="font-mono text-fg-secondary">{t.sku}</code>
        </KeyValue>
        <KeyValue label={tr("portal.repairs.labelSerial")}>
          <code className="font-mono text-fg-secondary">
            {t.serialNumber ?? "—"}
          </code>
        </KeyValue>
        <KeyValue label={tr("portal.repairs.labelWarranty")}>
          <WarrantyPill warranty={t.warranty} />
        </KeyValue>
        <KeyValue label={tr("portal.repairs.labelRoutedTo")}>
          <span className="text-fg-secondary">{t.vendor ?? "—"}</span>
        </KeyValue>
        {t.glsTrackingNumber && (
          <KeyValue label={tr("portal.repairs.labelGlsReturnLabel")}>
            <span className="font-mono text-fg-secondary">
              {t.glsTrackingNumber}
            </span>
          </KeyValue>
        )}
        {t.internalNotes && (
          <div className="space-y-1 border-t border-border pt-3">
            <div className="text-[10px] font-510 uppercase tracking-wider text-fg-tertiary">
              {tr("portal.repairs.internalNotes")}
            </div>
            <p className="text-xs text-fg-secondary">{t.internalNotes}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function KeyValue({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1 text-xs">
      <div className="text-[10px] font-510 uppercase tracking-wider text-fg-quaternary">
        {label}
      </div>
      <div>{children}</div>
    </div>
  );
}

// ============================================================================
// New ticket form
// ============================================================================

interface NewRepairFormProps {
  customerId: string;
  customerName: string;
  products: typeof DEMO_PRODUCTS;
  onCancel: () => void;
  onSubmit: (created: RepairTicket) => void;
}

function NewRepairForm({
  customerId,
  customerName,
  products,
  onCancel,
  onSubmit,
}: NewRepairFormProps) {
  const { t } = useTranslation();
  const [sku, setSku] = useState(products[0]?.sku ?? "");
  const product = products.find((p) => p.sku === sku) ?? products[0]!;
  const [serial, setSerial] = useState("");
  const [description, setDescription] = useState("");
  const [warranty, setWarranty] = useState<RepairTicket["warranty"]>("in-warranty");
  const [touched, setTouched] = useState(false);

  const descriptionOk = description.trim().length >= 20;
  const errors = {
    serial: serial.trim().length === 0,
    description: !descriptionOk,
  };
  const hasErrors = Object.values(errors).some(Boolean);

  function commit(e?: React.FormEvent) {
    e?.preventDefault();
    setTouched(true);
    if (hasErrors) return;
    const created: RepairTicket = {
      id: "TEMP",
      customerId,
      customerName,
      sku: product.sku,
      productName: product.name,
      ...(serial.trim() ? { serialNumber: serial.trim() } : {}),
      description: description.trim(),
      warranty,
      status: "open",
      internalNotes: "",
      timeline: [],
    };
    onSubmit(created);
  }

  return (
    <form onSubmit={commit} className="space-y-4">
      <div>
        <h2 className="text-sm font-510 text-fg-primary">{t("portal.repairs.newRepairTicket")}</h2>
        <p className="mt-0.5 text-xs text-fg-tertiary">
          {t("portal.repairs.formIntro")}
        </p>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1.5 block text-[10px] font-510 uppercase tracking-wider text-fg-tertiary">
            {t("portal.repairs.labelProduct")}
          </label>
          <select
            value={sku}
            onChange={(e) => setSku(e.target.value)}
            className="h-9 w-full rounded border border-border bg-bg-surface px-3 text-sm text-fg-primary focus:border-benchmark-teal/60 focus:outline-none"
          >
            {products.map((p) => (
              <option key={p.sku} value={p.sku}>
                {p.name} ({p.sku})
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1.5 block text-[10px] font-510 uppercase tracking-wider text-fg-tertiary">
            {t("portal.repairs.labelSerialNumberOptional")}
          </label>
          <Input
            value={serial}
            onChange={(e) => setSerial(e.target.value)}
            placeholder={t("portal.repairs.serialPlaceholder")}
          />
        </div>
      </div>
      <div>
        <label className="mb-1.5 block text-[10px] font-510 uppercase tracking-wider text-fg-tertiary">
          {t("portal.repairs.labelWarrantyStatus")}
          </label>
        <div className="flex flex-wrap gap-2">
          {(["in-warranty", "extended-warranty", "out-of-warranty"] as const).map(
            (w) => (
              <button
                key={w}
                type="button"
                onClick={() => setWarranty(w)}
                aria-pressed={warranty === w}
                className={cn(
                  "rounded-md border px-3 py-1.5 text-xs transition-colors",
                  warranty === w
                    ? "border-benchmark-teal/50 bg-benchmark-teal/10 text-fg-primary"
                    : "border-border bg-bg-surface text-fg-secondary hover:border-benchmark-teal/30 hover:text-fg-primary",
                )}
              >
                {t(WARRANTY_KEY[w])}
              </button>
            ),
          )}
        </div>
      </div>
      <div>
        <label className="mb-1.5 block text-[10px] font-510 uppercase tracking-wider text-fg-tertiary">
          {t("portal.repairs.labelWhatsWrong")}
          </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={4}
          placeholder={t("portal.repairs.descriptionPlaceholder")}
          className="w-full rounded border border-border bg-bg-surface px-3 py-2 text-sm text-fg-primary placeholder:text-fg-quaternary focus:border-benchmark-teal/60 focus:outline-none"
        />
        {touched && errors.description && (
          <p className="mt-1 text-[10px] text-accent-rose">
            {t("portal.repairs.descriptionError")}
          </p>
        )}
      </div>
      <div className="flex items-center justify-between border-t border-border pt-3">
        <p className="text-[10px] text-fg-quaternary">
          {t("portal.repairs.saveHint")}
        </p>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onCancel}>
            {t("portal.repairs.cancel")}
          </Button>
          <Button type="submit" size="sm" disabled={touched && hasErrors}>
            <Check className="h-3.5 w-3.5" strokeWidth={1.5} />
            {t("portal.repairs.saveRepairTicket")}
          </Button>
        </div>
      </div>
    </form>
  );
}
